import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiClientError } from '../../lib/api';
import { FormRow, PrimaryButton, SettingsCard, SuccessHint, inputStyle } from './_shared';

const MODEL_OPTIONS = [
  { value: 'claude-sonnet-4-6', label: 'Claude Sonnet 4.6 (consigliato)' },
  { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5 (più economico)' },
  { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (legacy)' },
];

export function LlmPage() {
  const qc = useQueryClient();
  const settingsQuery = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.getSettings(),
  });

  const [mode, setMode] = useState<'managed' | 'client'>('managed');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [temperature, setTemperature] = useState(0.7);
  const [maxTokens, setMaxTokens] = useState(1024);
  const [apiKeyInput, setApiKeyInput] = useState('');
  const [keyError, setKeyError] = useState<string | null>(null);
  const [keyStatusMsg, setKeyStatusMsg] = useState<string | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);

  useEffect(() => {
    if (!settingsQuery.data) return;
    const l = settingsQuery.data.settings.llm;
    setMode(l.mode);
    setModel(l.model);
    setTemperature(l.temperature);
    setMaxTokens(l.max_tokens);
  }, [settingsQuery.data]);

  const llmConfig = settingsQuery.data?.settings.llm;

  const saveLlmMutation = useMutation({
    mutationFn: () => api.updateLlm({ mode, model, temperature, max_tokens: maxTokens }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
    },
    onError: (err) => setKeyError(err instanceof ApiClientError ? err.payload.error : 'Errore'),
  });

  const setKeyMutation = useMutation({
    mutationFn: (key: string) => api.setLlmClientKey(key),
    onSuccess: (data) => {
      setApiKeyInput('');
      setKeyError(null);
      setKeyStatusMsg(`✓ Validata (${data.latency_ms}ms) — ${data.preview}`);
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
    onError: (err) => {
      setKeyError(err instanceof ApiClientError ? err.payload.error : 'Errore');
      setKeyStatusMsg(null);
    },
  });

  const removeKeyMutation = useMutation({
    mutationFn: () => api.removeLlmClientKey(),
    onSuccess: () => {
      setMode('managed');
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] });
    },
  });

  return (
    <div className="space-y-6">
      <SettingsCard
        kicker="LLM · API key"
        title="API key Anthropic"
        description="Audiq usa Claude per la conversazione. Scegli se usare la chiave gestita Arkenù (default) o la tua personale."
      >
        <div className="space-y-3">
          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="llm-mode"
              checked={mode === 'managed'}
              onChange={() => setMode('managed')}
              className="mt-1"
            />
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                API key gestita (default)
              </div>
              <div className="text-xs" style={{ color: 'var(--mute)' }}>
                Usa la chiave Arkenù. I costi LLM sono inclusi nel canone Audiq.
              </div>
            </div>
          </label>

          <label className="flex items-start gap-2">
            <input
              type="radio"
              name="llm-mode"
              checked={mode === 'client'}
              onChange={() => setMode('client')}
              disabled={!llmConfig?.client_api_key_set}
              className="mt-1"
            />
            <div>
              <div className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
                Usa la mia API key Anthropic
              </div>
              <div className="text-xs" style={{ color: 'var(--mute)' }}>
                Anthropic fattura direttamente a te. Firma il loro DPA dal tuo account.
                {!llmConfig?.client_api_key_set && ' Imposta prima una key sotto.'}
              </div>
            </div>
          </label>

          {llmConfig?.client_api_key_set && (
            <div
              className="rounded-md border-l-4 p-3 text-sm"
              style={{
                borderColor: 'var(--primary)',
                background: 'color-mix(in oklab, var(--primary) 8%, transparent)',
                color: 'var(--ink)',
              }}
            >
              <div className="mono">✓ Key configurata: {llmConfig.client_api_key_preview}</div>
              <button
                onClick={() => removeKeyMutation.mutate()}
                disabled={removeKeyMutation.isPending}
                className="mt-2 text-xs underline"
                style={{ color: 'var(--danger)' }}
              >
                Rimuovi key e torna a gestita
              </button>
            </div>
          )}

          <FormRow
            label={llmConfig?.client_api_key_set ? 'Sostituisci API key' : 'Inserisci API key'}
            hint='Inizia con "sk-ant-". Verifichiamo con un ping a Haiku 4.5 prima di salvare.'
          >
            <div className="flex gap-2">
              <input
                type="password"
                placeholder="sk-ant-api03-..."
                value={apiKeyInput}
                onChange={(e) => setApiKeyInput(e.target.value)}
                className="mono flex-1 rounded-md border px-3 py-2 text-sm"
                style={inputStyle()}
              />
              <PrimaryButton
                type="button"
                onClick={() => setKeyMutation.mutate(apiKeyInput)}
                disabled={!apiKeyInput || setKeyMutation.isPending}
              >
                {setKeyMutation.isPending ? 'Verifica…' : 'Verifica e salva'}
              </PrimaryButton>
            </div>
          </FormRow>

          {keyStatusMsg && (
            <div className="text-sm" style={{ color: 'var(--primary)' }}>
              {keyStatusMsg}
            </div>
          )}
          {keyError && (
            <div className="text-sm" style={{ color: 'var(--danger)' }}>
              {keyError}
            </div>
          )}
        </div>
      </SettingsCard>

      <SettingsCard
        kicker="LLM · parametri"
        title="Modello e parametri"
        description="Cambiando questi valori, le nuove sessioni li useranno; le esistenti completano col vecchio."
        rightSlot={<SuccessHint visible={showSuccess} />}
      >
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <FormRow label="Modello">
            <select
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={model}
              onChange={(e) => setModel(e.target.value)}
            >
              {MODEL_OPTIONS.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </select>
          </FormRow>

          <FormRow
            label={`Temperature (${temperature.toFixed(2)})`}
            hint="0 = deterministico · 1 = creativo · 0.7 = equilibrio"
          >
            <input
              type="range"
              min={0}
              max={1}
              step={0.05}
              value={temperature}
              onChange={(e) => setTemperature(Number(e.target.value))}
              className="w-full"
            />
          </FormRow>

          <FormRow label="Max tokens per turno" hint="Limita la lunghezza della risposta">
            <input
              type="number"
              min={64}
              max={8192}
              step={64}
              className="w-full rounded-md border px-3 py-2 text-sm"
              style={inputStyle()}
              value={maxTokens}
              onChange={(e) => setMaxTokens(Number(e.target.value))}
            />
          </FormRow>
        </div>

        <div className="mt-4">
          <PrimaryButton
            onClick={() => saveLlmMutation.mutate()}
            disabled={saveLlmMutation.isPending}
          >
            {saveLlmMutation.isPending ? 'Salvo…' : 'Salva LLM'}
          </PrimaryButton>
        </div>
      </SettingsCard>
    </div>
  );
}
